import {useTranslation} from '@/translations/i18n'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {OtherDataCardInEditModeType} from '@/pages/project-details-other-data/ProjectDetailsOtherData'
import {useGetProjectSocialWelfareConditions} from '../../queries/useGetProjectSocialWelfareConditions'
import {ProjectExtended} from '../../types'
import {useOutletContext} from 'react-router-dom'
import {SocialWelfareConditionsCardView} from '../social-welfare-conditions-view/SocialWelfareConditionsCardView'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {SocialWelfareConditionsCardEmptyState} from '../social-welfare-conditions-view/SocialWelfareConditionsCardEmptyState'
import {SocialWelfareConditionsCardForm} from '../social-welfare-conditions-form/SocialWelfareConditionsCardForm'
import {useMemo} from 'react'
interface SocialWelfareConditionsSectionProps {
    cardInEditMode: OtherDataCardInEditModeType
    setCardInEditMode: (cardInEditMode: OtherDataCardInEditModeType | null) => void
}
export const SocialWelfareConditionsSection = ({
    cardInEditMode,
    setCardInEditMode
}: SocialWelfareConditionsSectionProps) => {
    const {t} = useTranslation()
    const {project, isEditable} = useOutletContext<{project: ProjectExtended; isEditable: boolean}>()

    const isEvolutionaryAge = project.patient.serviceType === 'evolutionary_age'

    const {
        data: socialWelfareConditions,
        isLoading: isLoadingSocialWelfareConditions,
        error: errorSocialWelfareConditions,
        isError: isErrorSocialWelfareConditions
    } = useGetProjectSocialWelfareConditions({
        urlParams: {projectId: project.id},
        options: {
            retry: false
        }
    })

    const handleCancel = () => {
        setCardInEditMode(null)
    }

    const handleEdit = () => {
        setCardInEditMode('social_welfare_conditions')
    }

    const isActive = cardInEditMode === 'social_welfare_conditions'

    const defaultValues = useMemo(() => {
        if (socialWelfareConditions) {
            return {
                percentagePaid: socialWelfareConditions?.percentagePaid,
                socialRehabilitationTreatments: socialWelfareConditions?.socialRehabilitationTreatments,
                networkSupport: socialWelfareConditions?.networkSupport ?? undefined,
                caregiver: socialWelfareConditions?.caregiver,
                activityAutonomy: socialWelfareConditions?.activityAutonomy ?? undefined,
                mobilityAutonomy: socialWelfareConditions?.mobilityAutonomy ?? undefined,
                applicant: socialWelfareConditions?.applicant,
                parentalResponsibility: socialWelfareConditions?.parentalResponsibility,
                sharedCarePlanning: socialWelfareConditions?.sharedCarePlanning
            }
        } else {
            return {
                applicant: 'regional_services' as const,
                sharedCarePlanning: true,
                percentagePaid: 100,
                socialRehabilitationTreatments: false,
                networkSupport: 'presence' as const
            }
        }
    }, [socialWelfareConditions])

    if (isActive) {
        return (
            <SocialWelfareConditionsCardForm
                isEvolutionaryAge={isEvolutionaryAge}
                onCancel={handleCancel}
                defaultValues={defaultValues}
                isEdit={!!socialWelfareConditions}
            />
        )
    }
    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:social_welfare_conditions')}
            isDisabled={cardInEditMode !== null}
            headingEndSlot={
                isEditable && (
                    <EditDetailsSectionButtons
                        isActive={isActive}
                        isDisabled={isLoadingSocialWelfareConditions}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                    />
                )
            }
        >
            {isErrorSocialWelfareConditions &&
            errorSocialWelfareConditions?.response?.data.code !== 'project-social-welfare-condition-not-found' ? (
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
            ) : socialWelfareConditions ? (
                <SocialWelfareConditionsCardView
                    isEvolutionaryAge={isEvolutionaryAge}
                    socialWelfareConditions={socialWelfareConditions}
                />
            ) : (
                <SocialWelfareConditionsCardEmptyState
                    isEvolutionaryAge={isEvolutionaryAge}
                    isLoading={isLoadingSocialWelfareConditions}
                />
            )}
        </DetailsFieldsSection>
    )
}
